import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import LiveWatch from "@/pages/LiveWatch";
import Patrol from "@/pages/Patrol";
import VisionAnalyzer from "@/pages/VisionAnalyzer";
import Employees from "@/pages/Employees";
import MovementMap from "@/pages/MovementMap";
import Customers from "@/pages/Customers";
import HotZones from "@/pages/HotZones";
import StoreHeatMap from "@/pages/StoreHeatMap";
import Cameras from "@/pages/Cameras";
import CameraConnect from "@/pages/CameraConnect";
import EdgeAgent from "@/pages/EdgeAgent";
import Settings from "@/pages/Settings";
import Incidents from "@/pages/Incidents";
import Planner from "@/pages/Planner";
import AreaIntel from "@/pages/AreaIntel";
import GlobalWatch from "@/pages/GlobalWatch";
import MapView from "@/pages/MapView";
import StoreBoard from "@/pages/StoreBoard";
import Architecture from "@/pages/Architecture";
import SurveillanceWall from "@/pages/SurveillanceWall";
import AlertSpotlight from "@/pages/AlertSpotlight";
import CommandBrain from "@/pages/CommandBrain";
import People from "@/pages/People";
import WorkAreas from "@/pages/WorkAreas";
import TvKiosk from "@/pages/TvKiosk";
import TvDisplays from "@/pages/TvDisplays";
import OfficeBoard from "@/pages/OfficeBoard";
import AvStoneCameras from "@/pages/AvStoneCameras";
import AvStoneDashboard from "@/pages/AvStoneDashboard";
import AvStoneLive from "@/pages/AvStoneLive";
import AvStonePlates from "@/pages/AvStonePlates";
import AvStoneLoading from "@/pages/AvStoneLoading";
import AvStoneStudy from "@/pages/AvStoneStudy";
import HotZoneTv from "@/pages/HotZoneTv";
import Events from "@/pages/Events";
import Eye from "@/pages/Eye";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C]">
        <div className="font-mono-data text-sm text-[#71717A] tracking-widest uppercase">Initializing SentryVision…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/hotzone-tv" element={<HotZoneTv />} />
      <Route path="/tv" element={<ProtectedRoute><TvKiosk /></ProtectedRoute>} />
      <Route path="/cam" element={<ProtectedRoute><TvKiosk /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><TvKiosk /></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><TvKiosk /></ProtectedRoute>} />
      <Route path="/office" element={<ProtectedRoute><OfficeBoard /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="watch" element={<LiveWatch />} />
        <Route path="wall" element={<SurveillanceWall />} />
        <Route path="spotlight" element={<AlertSpotlight />} />
        <Route path="brain" element={<CommandBrain />} />
        <Route path="people" element={<People />} />
        <Route path="areas" element={<WorkAreas />} />
        <Route path="displays" element={<TvDisplays />} />
        <Route path="patrol" element={<Patrol />} />
        <Route path="analyzer" element={<VisionAnalyzer />} />
        <Route path="incidents" element={<Incidents />} />
        <Route path="events" element={<Events />} />
        <Route path="eye" element={<Eye />} />
        <Route path="employees" element={<Employees />} />
        <Route path="movement" element={<MovementMap />} />
        <Route path="customers" element={<Customers />} />
        <Route path="hotzones" element={<HotZones />} />
        <Route path="store-heatmap" element={<StoreHeatMap />} />
        <Route path="cameras" element={<Cameras />} />
        <Route path="connect" element={<CameraConnect />} />
        <Route path="agent" element={<EdgeAgent />} />
        <Route path="planner" element={<Planner />} />
        <Route path="intel" element={<AreaIntel />} />
        <Route path="global" element={<GlobalWatch />} />
        <Route path="map" element={<MapView />} />
        <Route path="board" element={<StoreBoard />} />
        <Route path="settings" element={<Settings />} />
        <Route path="architecture" element={<Architecture />} />
        <Route path="avstone" element={<AvStoneCameras />} />
        <Route path="avstone/dashboard" element={<AvStoneDashboard />} />
        <Route path="avstone/live" element={<AvStoneLive />} />
        <Route path="avstone/plates" element={<AvStonePlates />} />
        <Route path="avstone/loading" element={<AvStoneLoading />} />
        <Route path="avstone/study" element={<AvStoneStudy />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
